import {concatenate} from "../../../services/string";
import {Menu} from "@headlessui/react";
import {EyeOffIcon} from "@heroicons/react/outline";
import React from "react";
import {Event} from '../../../types/Event'
import {DateTime} from "luxon";
import {FIVE_MINUTE_SLOTS} from "./DayView";
import {CalendarView} from "./EventSchedule";
import EventPreview from "../../../components/EventPreview";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type EventProps = {
  event: Event
  eventList: Array<Event>
  selectedDate: DateTime
  calendarView: CalendarView
  classNames?: string
  onDeleteEvent: (event: Event) => void
}

// Helpers ----

function eventStyle(calendarView: CalendarView, events: Array<Event>, event: Event, maxCol: number = FIVE_MINUTE_SLOTS) {
  if (event.is_all_day) {
    return {
      gridRow: '1',
    }
  }

  let start = Math.floor(((event.starts_at.hour * 60) + event.starts_at.minute) / 5) + 2
  let span = Math.ceil((event.duration.value / 60) / 5)

  if (start + span > maxCol) {
    span = maxCol - start
  }

  const concurrentEvents = eventsDuring(events, event.starts_at, event.ends_at)
  const concurrentEventCount = concurrentEvents.length || 1
  const concurrentEventIndex = concurrentEvents.findIndex(concurrentEvent => concurrentEvent.id === event.id)

  if (calendarView === 'day') {
    return {
      gridRow: `${start} / span ${span}`,
    }
  }

  if (calendarView === 'week') {
    return {
      gridRow: `${start} / span ${span}`,
      width: `${100 / concurrentEventCount}%`,
      marginLeft: `${(concurrentEventIndex) * (100 / concurrentEventCount)}%`
    }
  }
}

function eventsDuring(events: Array<Event>, start: DateTime, end: DateTime): Array<Event> {
  return events.filter(event => {
    // Starts during
    if ((event.starts_at >= start) && (event.starts_at <= end)) {
      return true
    }

    // Ends during
    if ((event.ends_at >= start) && (event.ends_at <= end)) {
      return true
    }

    // Starts before and ends after
    return (event.starts_at < start) && (event.ends_at > end);
  })
}

// Components ----

function EventItem({event, eventList, selectedDate, calendarView, classNames = '', onDeleteEvent}: EventProps) {
  const {settings} = useOperator();
  const now = DateTime.now();

  return (
    <li className={concatenate(classNames, event.starts_at.startOf('day').equals(selectedDate) ? 'flex' : 'hidden sm:flex', 'relative mt-px')}
        style={eventStyle(calendarView, eventList || [], event)}
        key={event.id}
    >
      <Menu as="div">
        <Menu.Button className={concatenate(
          event.is_all_day || event.duration.value < 1800 ? 'px-2' : 'p-2',
          event.ends_at > now ? 'bg-brand hover:bg-gradient-white-overlay focus-visible:outline-offset-4 focus-visible:outline-brand' : 'bg-gray-400 hover:opacity-90',
          'group absolute inset-1 flex flex-col rounded-lg text-xs leading-5 overflow-hidden')}
        >
          <div className="order-1 font-semibold text-left text-gray-50 flex space-x-1 items-center">
            {!event.is_published && <EyeOffIcon className="h-3 w-3 text-gray-200"/>}
            <span>{event.name}</span>
          </div>

          {!event.is_all_day && event.duration.value >= 1800 ?
            <>
              <p className="order-2 text-gray-300 text-left">{event.type.name}</p>
              <p className="text-gray-200 group-hover:text-gray-100">
                <time
                  dateTime={event.starts_at.setZone(event.timezone).toISO()}>{event.starts_at.setZone(event.timezone).setLocale(settings?.localisation.locale ?? 'local').toLocaleString(DateTime.TIME_SIMPLE)}
                </time>
              </p>
            </>
            : null}
        </Menu.Button>

        <EventPreview event={event} onDeleteEvent={onDeleteEvent} />
      </Menu>
    </li>
  )
}

export default EventItem